import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Box,
  TextField,
  Autocomplete,
  IconButton,
  Divider,
  ListItem,
  ListItemText,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "../../components/RosterConfig/RosterConfigStyle";
import classes from "../OMT/OMTBulkEdit/Common.module.css";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import applicationUsageLog from "../../utils/applicationUsagesLog";

import {
  getOSRApprovingAuthorityEmailIds,
  submitOvernightShippingRequest,
} from "services/userServices";

const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "0.10em",
  paddingBottom: theme.spacing(1),
  color: theme.palette.primary.main,
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));

const shippingTypeOptions = [
  {
    value: "3-Day Shipping",
    label: "3-Day Shipping",
    description:
      "$30-$40 (Standard for all deployments - 9 business days from lock to Day 1)",
  },
  {
    value: "2-Day Shipping",
    label: "2-Day Shipping",
    description:
      "$40-$50 (Requires SVP approval if not for Licensed Healthcare)",
  },
  {
    value: "Standard Overnight Shipping",
    label: "Standard Overnight Shipping",
    description: "$60-$80 (Requires SVP approval)",
  },
];

// Each department has its own "Additional Information"
const departmentConfig = {
  "General & Administrative": {
    reasonOptions: [
      { value: "hiring_manager_request", label: "Hiring manager request" },
      { value: "delay_in_hiring", label: "Delay in hiring" },
      { value: "other", label: "Other - elaborate on the next question" },
    ],
    elaborationExample:
      "e.g Hiring manager askes for expedited shipping to start the new hire shortly after they are hired.",
    hasBillableQuestion: false,
  },
  "ISU / Desktop Support / Logistics": {
    reasonOptions: [
      { value: "provisioning_delay", label: "Provisioning delay" },
      { value: "technical_issues", label: "Technical issues" },
      { value: "imaging_delay", label: "Imaging Delay" },
      { value: "break_fix", label: "Break Fix" },
      { value: "equipment_inventory", label: "Equipment Inventory" },
      { value: "shipping_errors", label: "Shipping Errors" },
      { value: "shipping_vendor_issues", label: "Shipping Vendor Issues" },
      { value: "other", label: "Other - elaborate on the next question" },
    ],
    elaborationExample:
      "e.g System outage that prevents provisioning. Ticket was kicked to manual intervention and not addressed within the SLA.",
    hasBillableQuestion: false,
  },
  "Client Operations": {
    reasonOptions: [
      {
        value: "non_compliance",
        label: "Non-Compliance (request outside of Key standards)",
      },
      { value: "client_delay_or_request", label: "Client Delay or request" },
      { value: "other", label: "Other - elaborate on the next question" },
    ],
    elaborationExample:
      "e.g unexpected attrition causes need to quickly hire replacements.",
    hasBillableQuestion: true,
  },
  "People & Culture": {
    reasonOptions: [
      { value: "returning_from_leave", label: "Employee returning from leave" },
      { value: "other", label: "Other - elaborate on the next question" },
    ],
    elaborationExample:
      "e.g employee ready to return from leave but requires a new PC.",
    hasBillableQuestion: false,
  },
  "Talent Acquisition": {
    reasonOptions: [
      { value: "late_roster_lock", label: "Late roster lock" },
      { value: "ticket_errors", label: "Ticket errors" },
      { value: "other", label: "Other - elaborate on the next question" },
    ],
    elaborationExample:
      "e.g additional time needed to fill class. No active AD account and requires ticket to be resubmitted after roster lock day.",
    hasBillableQuestion: false,
  },
};

const departmentOptions = Object.keys(departmentConfig);

const initialFormState = {
  numberOfShipments: 1,
  shippingType: "",
  department: "",
  isBillableToClient: "",
  expeditedReason: "",
  expeditedReasonDetails: "",
};

export default function OvernightShippingRequest({
  open,
  classRequestId,
  parentClientId,
  parentClientName,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(initialFormState);
  const { t } = useTranslation(["OvernightShippingRequest"]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  //Reset the form whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm(initialFormState);

      // Log application usage
      applicationUsageLog(
        localStorage.getItem("User"),
        localStorage.getItem("EmailId"),
        "ASTT",
        "Expedited Equipment Shipment Request",
      );
    }
  }, [open, classRequestId]);

  const parentClientDisplay = `${parentClientName || "UNKNOWN"} (${
    parentClientId || "99999"
  })`;

  const activeDepartmentConfig = form.department
    ? departmentConfig[form.department]
    : null;

  const isThreeDayShipping = form.shippingType === "3-Day Shipping";

  const isFormValid = isThreeDayShipping
    ? !!form.shippingType
    : !!form.shippingType &&
      !!form.department &&
      !!form.expeditedReason &&
      form.expeditedReasonDetails.trim().length >= 100 &&
      (!activeDepartmentConfig?.hasBillableQuestion ||
        !!form.isBillableToClient);

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const showSnack = (message, severity) => {
    dispatch(
      SnackBarStoreAction.updateSnack({ open: true, message, severity }),
    );
  };

  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  const handleDepartmentChange = (event, newValue) => {
    setForm((prev) => ({
      ...prev,
      department: newValue || "",
      isBillableToClient: "",
      expeditedReason: "",
      expeditedReasonDetails: "",
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isThreeDayShipping) {
        const payload = createData(null);
        const response = await submitOvernightShippingRequest(
          classRequestId,
          parentClientId,
          payload,
        );

        if (response?.message === "Success") {
          onSubmit?.(payload);
          showSnack("Request submitted successfully", "success");
          onClose();
        } else {
          showSnack(
            "Something went wrong submitting the request. Please try again.",
            "error",
          );
        }
      } else {
        const approvalAuthorityResponse =
          await getOSRApprovingAuthorityEmailIds(
            parentClientId,
            form.department,
          );

        const approverEmailList =
          approvalAuthorityResponse?.data?.[0]?.ApproverEmailList;

        if (
          approvalAuthorityResponse?.message === "Success" &&
          approverEmailList
        ) {
          const payload = createData(approverEmailList);
          const response = await submitOvernightShippingRequest(
            classRequestId,
            parentClientId,
            payload,
          );

          if (response?.message === "Success") {
            onSubmit?.(payload);
            showSnack("Request submitted successfully", "success");
            onClose();
          } else {
            showSnack(
              "Something went wrong submitting the request. Please try again.",
              "error",
            );
          }
        } else {
          showSnack(
            "No Approval Authority listed. Please reach out ASTTDevelopment@ttec.com.",
            "error",
          );
        }
      }
    } catch (error) {
      console.error("Request failed:", error);
      showSnack("Request failed due to a network or server error.", "error");
    } finally {
      setLoading(false);
    }
  };

  const createData = (OSRApprovingAuthorityEmailIds) => {
    return {
      ApprovalAuthority: OSRApprovingAuthorityEmailIds,
      ParentClientCode: parentClientId,
      ClassRequestId: classRequestId,
      ShipmentType: form.shippingType,
      TotalShipmentRequired: Number(form.numberOfShipments),
      ShipmentRequestedBy: form.department || "",
      ParentClientName: parentClientName,
      IsBilableToClient: form.isBillableToClient === "yes",
      ShipmentReason: form.expeditedReason || "",
      Remarks: form.expeditedReasonDetails || "",
      ApprovalRequired: !isThreeDayShipping,
      ResponderName: localStorage.getItem("EmailId"),
    };
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
        <DialogTitle>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <MainHeading>{t("heading")}</MainHeading>
              <IconButton
                aria-label="close"
                onClick={handleCancel}
                // disabled={submitting}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("headingDescription")}
          </Typography>

          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            <li>
              <Typography variant="body2">
                <strong>{t("3days")}</strong> {t("3daysSentence")}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>{t("2days")}</strong> {t("2daysSentence")}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>{t("StandardOvernightShipping")}</strong>{" "}
                {t("StandardOvernightShippingSentence")}
              </Typography>
            </li>
          </Box>

          <Typography
            variant="subtitle1"
            sx={{ color: "error.main", fontWeight: "bold", mb: 2 }}
          >
            {t("subtitle1")}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <SubHeading>
                {t("ClassRequestId")}{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </SubHeading>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={classRequestId ?? ""}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "action.hover" }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <SubHeading>
                {t("ParentClient")}{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </SubHeading>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={parentClientDisplay}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "action.hover" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="subtitle1"
                sx={{ color: "#1976D2", fontWeight: "bold" }}
              >
                {t("subtitle2")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <SubHeading>
                {t("NumberofshipmentsNeeded")}{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </SubHeading>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
              >
                {t("NumberofshipmentsNeededDescription")}
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="outlined"
                inputProps={{ min: 1 }}
                value={form.numberOfShipments}
                onChange={handleFieldChange("numberOfShipments")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <SubHeading>
                {t("WhatTypeOfShippingNeeded")}{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </SubHeading>
              <Autocomplete
                disablePortal
                blurOnSelect
                options={shippingTypeOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={
                  shippingTypeOptions.find(
                    (option) => option.value === form.shippingType,
                  ) || null
                }
                onChange={(event, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    shippingType: newValue?.value || "",
                    // 3-Day Shipping skips the department/reason section entirely,so clear other all values
                    ...(newValue?.value === "3-Day Shipping"
                      ? {
                          department: "",
                          isBillableToClient: "",
                          expeditedReason: "",
                          expeditedReasonDetails: "",
                        }
                      : {}),
                  }))
                }
                renderOption={(props, option) => (
                  <ListItem {...props} key={option.value}>
                    <ListItemText
                      primary={option.label}
                      secondary={option.description}
                    />
                  </ListItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select shipping type"
                  />
                )}
              />
              {form.shippingType && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                >
                  {
                    shippingTypeOptions.find(
                      (option) => option.value === form.shippingType,
                    )?.description
                  }
                </Typography>
              )}
            </Grid>

            {/* Skipped entirely for 3-Day Shipping — no additional info is needed in
              that case*/}
            {!!form.shippingType && !isThreeDayShipping && (
              <Grid item xs={12} sm={6}>
                <SubHeading>
                  {t("WhatIsYourDepartment")}{" "}
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                </SubHeading>
                <Autocomplete
                  disablePortal
                  blurOnSelect
                  options={departmentOptions}
                  value={form.department || null}
                  onChange={handleDepartmentChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="Select department"
                    />
                  )}
                />
              </Grid>
            )}

            {!isThreeDayShipping && activeDepartmentConfig && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "#1976D2", fontWeight: "bold" }}
                  >
                    {form.department} - {t("AdditionalInformation")}
                  </Typography>
                </Grid>

                {activeDepartmentConfig.hasBillableQuestion && (
                  <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset">
                      <SubHeading>
                        {t("ClientBilliable")}{" "}
                        <Box component="span" sx={{ color: "error.main" }}>
                          *
                        </Box>
                      </SubHeading>
                      <RadioGroup
                        row
                        value={form.isBillableToClient}
                        onChange={handleFieldChange("isBillableToClient")}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <SubHeading>
                    {`${t("Reason")} ${form.department}?`}{" "}
                    <Box component="span" sx={{ color: "error.main" }}>
                      *
                    </Box>
                  </SubHeading>
                  <Autocomplete
                    disablePortal
                    blurOnSelect
                    options={activeDepartmentConfig.reasonOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    value={
                      activeDepartmentConfig.reasonOptions.find(
                        (option) => option.value === form.expeditedReason,
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      setForm((prev) => ({
                        ...prev,
                        expeditedReason: newValue?.value || "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        placeholder="Select a reason"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <SubHeading>
                    {t("SpecificDetails")}{" "}
                    <Box component="span" sx={{ color: "error.main" }}>
                      *
                    </Box>
                  </SubHeading>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                  >
                    {`${t("RequestSpecificDetails")} (${activeDepartmentConfig.elaborationExample})`}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    variant="outlined"
                    placeholder="Enter your answer (minimum 100 characters)"
                    value={form.expeditedReasonDetails}
                    onChange={handleFieldChange("expeditedReasonDetails")}
                    error={
                      form.expeditedReasonDetails.length > 0 &&
                      form.expeditedReasonDetails.trim().length < 100
                    }
                    helperText={`${form.expeditedReasonDetails.trim().length} / 100 characters minimum`}
                  />
                </Grid>
              </>
            )}
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <CustomButton
                variant="contained"
                className={classes.buttonSave}
                size="small"
                onClick={handleSubmit}
                disabled={!isFormValid}
              >
                {t("Submit")}
              </CustomButton>
              <CustomButton
                variant="outlined"
                className={classes.buttonReset}
                size="small"
                onClick={handleCancel}
              >
                {t("Cancel")}
              </CustomButton>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}

OvernightShippingRequest.propTypes = {
  open: PropTypes.bool.isRequired,
  classRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentClientName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
